import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchLambdaTryOnResults,
  findMatchingLambdaTryOnResult,
  isPendingLambdaTryOnStatus,
  isSuccessfulLambdaTryOnStatus,
} from '../../features/tryOnLambda';
import {
  getTryOnPollIntervalMilliseconds,
  getTryOnPollMaxMilliseconds,
  hasTryOnPollingTimedOut,
} from '../../features/tryOnPolling';
import {
  enqueuePendingTryOnTask,
  persistTryOnSession,
  readPersistedTryOnSession,
  readQueuedPendingTryOnTasks,
  removeQueuedPendingTryOnTask,
  TRY_ON_SESSION_CHANGE_EVENT,
  type GeneratedTryOnResultState,
  type PendingTryOnTaskState,
} from '../../features/tryOnSession';

const TRY_ON_POLL_INTERVAL_MS = getTryOnPollIntervalMilliseconds(
  __AI_POLL_INTERVAL__,
  5
);
const TRY_ON_POLL_MAX_MS = getTryOnPollMaxMilliseconds(
  __AI_POLL_MAX_SECONDS__,
  300
);

export default function TryOnSessionWatcher() {
  const location = useLocation();
  const isTryOnPollingOwnedByPage = location.pathname === '/try-on/upload';

  useEffect(() => {
    if (isTryOnPollingOwnedByPage) {
      return;
    }

    let isActive = true;
    let isPolling = false;
    let shouldRunPollImmediately = false;
    let pollTimeoutId: number | null = null;
    let lastPollStartedAt = 0;
    let lastPolledTaskKey = '';

    const clearScheduledPoll = () => {
      if (pollTimeoutId !== null) {
        window.clearTimeout(pollTimeoutId);
        pollTimeoutId = null;
      }
    };

    const schedulePoll = (delayMs: number) => {
      if (!isActive) {
        return;
      }

      clearScheduledPoll();
      pollTimeoutId = window.setTimeout(() => {
        pollTimeoutId = null;
        void pollTask();
      }, delayMs);
    };

    const getPendingTaskKey = () => {
      const queuedTask = readQueuedPendingTryOnTasks()[0] ?? null;
      if (queuedTask) {
        return `${queuedTask.provider}:${queuedTask.taskId}`;
      }

      const persistedSession = readPersistedTryOnSession();
      const pendingTryOnTask = persistedSession?.pendingTryOnTask ?? null;
      return pendingTryOnTask ? `${pendingTryOnTask.provider}:${pendingTryOnTask.taskId}` : '';
    };

    const requestPoll = () => {
      if (!isActive) {
        return;
      }

      clearScheduledPoll();

      if (isPolling) {
        shouldRunPollImmediately = true;
        return;
      }

      const pendingTaskKey = getPendingTaskKey();
      const elapsedSinceLastPoll = pendingTaskKey && pendingTaskKey === lastPolledTaskKey
        ? Date.now() - lastPollStartedAt
        : TRY_ON_POLL_INTERVAL_MS;
      const nextDelayMs = Math.max(0, TRY_ON_POLL_INTERVAL_MS - elapsedSinceLastPoll);

      schedulePoll(nextDelayMs);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key?.includes('vadauro_active_try_on_session') || event.key === null) {
        requestPoll();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestPoll();
      }
    };

    const handleSessionChange = () => {
      requestPoll();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(TRY_ON_SESSION_CHANGE_EVENT, handleSessionChange);
    window.addEventListener('focus', handleSessionChange);
    window.addEventListener('pageshow', handleSessionChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const scheduleNextPoll = () => {
      schedulePoll(TRY_ON_POLL_INTERVAL_MS);
    };

    const stopPollingForPendingTask = (
      persistedSession: NonNullable<ReturnType<typeof readPersistedTryOnSession>>
    ) => {
      if (!persistedSession.pendingTryOnTask) {
        return;
      }

      persistTryOnSession({
        shouldSaveAssetsToLibrary: persistedSession.shouldSaveAssetsToLibrary,
        uploadedTryOnPhoto: persistedSession.uploadedTryOnPhoto,
        generatedTryOnResult: persistedSession.generatedTryOnResult,
        pendingTryOnTask: null,
        pendingTryOnVideoTask: persistedSession.pendingTryOnVideoTask,
        generatedTryOnVideoResult: persistedSession.generatedTryOnVideoResult,
      });
    };

    const finalizeCompletedGeneratedPhoto = async (
      persistedSession: NonNullable<ReturnType<typeof readPersistedTryOnSession>>,
    ) => {
      const generatedTryOnResult = persistedSession.generatedTryOnResult;
      if (!generatedTryOnResult || !generatedTryOnResult.imageUrl) {
        return false;
      }

      stopPollingForPendingTask(persistedSession);
      return true;
    };

    const finalizeQueuedGeneratedPhoto = async (
      pendingTryOnTask: PendingTryOnTaskState,
      generatedTryOnResult: GeneratedTryOnResultState
    ) => {
      removeQueuedPendingTryOnTask(pendingTryOnTask.taskId, pendingTryOnTask.provider);

      const latestSession = readPersistedTryOnSession();
      const activePendingTask = latestSession?.pendingTryOnTask ?? null;
      const activeGeneratedResult = latestSession?.generatedTryOnResult ?? null;
      const shouldMirrorToActiveSession = Boolean(
        latestSession &&
        (
          activePendingTask?.taskId === pendingTryOnTask.taskId ||
          activeGeneratedResult?.taskId === pendingTryOnTask.taskId
        )
      );

      if (latestSession && shouldMirrorToActiveSession) {
        persistTryOnSession({
          shouldSaveAssetsToLibrary: latestSession.shouldSaveAssetsToLibrary,
          uploadedTryOnPhoto: latestSession.uploadedTryOnPhoto,
          generatedTryOnResult,
          pendingTryOnTask: null,
          pendingTryOnVideoTask: latestSession.pendingTryOnVideoTask,
          generatedTryOnVideoResult: latestSession.generatedTryOnVideoResult,
        });
      }

      return true;
    };

    const pollQueuedTask = async (
      pendingTryOnTask: PendingTryOnTaskState,
      persistedSession: ReturnType<typeof readPersistedTryOnSession>
    ) => {
      if (pendingTryOnTask.provider !== 'standard') {
        removeQueuedPendingTryOnTask(pendingTryOnTask.taskId, pendingTryOnTask.provider);
        return true;
      }

      if (hasTryOnPollingTimedOut(pendingTryOnTask.startedAt, TRY_ON_POLL_MAX_MS)) {
        removeQueuedPendingTryOnTask(pendingTryOnTask.taskId, pendingTryOnTask.provider);
        if (
          persistedSession?.pendingTryOnTask?.taskId === pendingTryOnTask.taskId &&
          persistedSession.pendingTryOnTask.provider === pendingTryOnTask.provider
        ) {
          stopPollingForPendingTask(persistedSession);
        }
        return true;
      }

      lastPolledTaskKey = `${pendingTryOnTask.provider}:${pendingTryOnTask.taskId}`;
      lastPollStartedAt = Date.now();

      const responseBody = await fetchLambdaTryOnResults(pendingTryOnTask.rawDataId ?? '', {
        activeInventoryIds: pendingTryOnTask.activeInventoryIds,
        forceRefresh: true,
        minRefreshIntervalMs: TRY_ON_POLL_INTERVAL_MS,
      });
      const matchingResult = findMatchingLambdaTryOnResult(responseBody, {
        resultId: pendingTryOnTask.taskId,
        inventoryIds: pendingTryOnTask.inventoryIds,
      });
      const status = matchingResult?.status ?? '';
      const resolvedInventoryIds = matchingResult?.inventoryIds?.length
        ? matchingResult.inventoryIds
        : pendingTryOnTask.inventoryIds;

      if ((isSuccessfulLambdaTryOnStatus(status) || Boolean(matchingResult?.imageUrl)) && matchingResult?.imageUrl) {
        const nextGeneratedTryOnResult: GeneratedTryOnResultState = {
          imageUrl: matchingResult.imageUrl,
          objectKey: matchingResult.objectKey ?? '',
          resultId: matchingResult.id || pendingTryOnTask.taskId,
          taskId: pendingTryOnTask.taskId,
          productName: pendingTryOnTask.productName,
          productImageUrl: pendingTryOnTask.productImageUrl,
          price: pendingTryOnTask.price,
          provider: pendingTryOnTask.provider,
          isSaved: matchingResult.isSaved,
          itemId: pendingTryOnTask.itemId,
          itemIds: pendingTryOnTask.itemIds,
          inventoryIds: resolvedInventoryIds,
          rawDataId: pendingTryOnTask.rawDataId ?? persistedSession?.uploadedTryOnPhoto?.rawDataId,
          sourceItems: pendingTryOnTask.sourceItems,
        };

        return finalizeQueuedGeneratedPhoto(pendingTryOnTask, nextGeneratedTryOnResult);
      }

      if (isSuccessfulLambdaTryOnStatus(status)) {
        removeQueuedPendingTryOnTask(pendingTryOnTask.taskId, pendingTryOnTask.provider);
        return true;
      }

      if (isPendingLambdaTryOnStatus(status) || !matchingResult) {
        scheduleNextPoll();
        return false;
      }

      removeQueuedPendingTryOnTask(pendingTryOnTask.taskId, pendingTryOnTask.provider);
      return true;
    };

    const pollTask = async () => {
      if (!isActive) {
        return;
      }

      if (isPolling) {
        shouldRunPollImmediately = true;
        return;
      }

      isPolling = true;
      const persistedSession = readPersistedTryOnSession();
      const pendingTryOnTask = persistedSession?.pendingTryOnTask ?? null;
      const generatedTryOnResult = persistedSession?.generatedTryOnResult ?? null;
      const hasGeneratedResultForPendingTask = Boolean(
        generatedTryOnResult &&
        (
          !pendingTryOnTask ||
          generatedTryOnResult.taskId === pendingTryOnTask.taskId
        )
      );

      try {
        if (pendingTryOnTask) {
          enqueuePendingTryOnTask(pendingTryOnTask);
        }

        const queuedPendingTask = readQueuedPendingTryOnTasks()[0] ?? null;
        if (queuedPendingTask) {
          const didFinishQueuedTask = await pollQueuedTask(queuedPendingTask, persistedSession);
          if (didFinishQueuedTask && readQueuedPendingTryOnTasks().length > 0) {
            schedulePoll(0);
          }
          return;
        }

        if (!isActive || !persistedSession) {
          return;
        }

        if (!pendingTryOnTask || hasGeneratedResultForPendingTask) {
          if (generatedTryOnResult && hasGeneratedResultForPendingTask) {
            void finalizeCompletedGeneratedPhoto(persistedSession).then((didSave) => {
              if (!didSave) {
                scheduleNextPoll();
              }
            });
          }
          return;
        }

        if (pendingTryOnTask.provider !== 'standard') {
          return;
        }

        lastPolledTaskKey = `${pendingTryOnTask.provider}:${pendingTryOnTask.taskId}`;
        lastPollStartedAt = Date.now();

        if (hasTryOnPollingTimedOut(pendingTryOnTask.startedAt, TRY_ON_POLL_MAX_MS)) {
          stopPollingForPendingTask(persistedSession);
          removeQueuedPendingTryOnTask(pendingTryOnTask.taskId, pendingTryOnTask.provider);
          return;
        }

        const responseBody = await fetchLambdaTryOnResults(pendingTryOnTask.rawDataId ?? '', {
          activeInventoryIds: pendingTryOnTask.activeInventoryIds,
          forceRefresh: true,
          minRefreshIntervalMs: TRY_ON_POLL_INTERVAL_MS,
        });
        const matchingResult = findMatchingLambdaTryOnResult(responseBody, {
          resultId: pendingTryOnTask.taskId,
          inventoryIds: pendingTryOnTask.inventoryIds,
        });
        const status = matchingResult?.status ?? '';
        const resolvedInventoryIds = matchingResult?.inventoryIds?.length
          ? matchingResult.inventoryIds
          : pendingTryOnTask.inventoryIds;

        if ((isSuccessfulLambdaTryOnStatus(status) || Boolean(matchingResult?.imageUrl)) && matchingResult?.imageUrl) {
          const nextGeneratedTryOnResult = {
            imageUrl: matchingResult.imageUrl,
            objectKey: matchingResult.objectKey ?? '',
            resultId: matchingResult.id || pendingTryOnTask.taskId,
            taskId: pendingTryOnTask.taskId,
            productName: pendingTryOnTask.productName,
            productImageUrl: pendingTryOnTask.productImageUrl,
            price: pendingTryOnTask.price,
            provider: pendingTryOnTask.provider,
            isSaved: matchingResult.isSaved,
            itemId: pendingTryOnTask.itemId,
            itemIds: pendingTryOnTask.itemIds,
            inventoryIds: resolvedInventoryIds,
            rawDataId: pendingTryOnTask.rawDataId ?? persistedSession.uploadedTryOnPhoto?.rawDataId,
            sourceItems: pendingTryOnTask.sourceItems,
          };
          const nextSession = {
            shouldSaveAssetsToLibrary: persistedSession.shouldSaveAssetsToLibrary,
            uploadedTryOnPhoto: persistedSession.uploadedTryOnPhoto,
            generatedTryOnResult: nextGeneratedTryOnResult,
            pendingTryOnTask,
            pendingTryOnVideoTask: persistedSession.pendingTryOnVideoTask,
            generatedTryOnVideoResult: persistedSession.generatedTryOnVideoResult,
          };

          persistTryOnSession(nextSession);
          void finalizeCompletedGeneratedPhoto({
            ...persistedSession,
            generatedTryOnResult: nextGeneratedTryOnResult,
            pendingTryOnTask,
          }).then((didSave) => {
            if (!didSave) {
              scheduleNextPoll();
            }
          });
          return;
        }

        if (isSuccessfulLambdaTryOnStatus(status)) {
          stopPollingForPendingTask(persistedSession);
          return;
        }

        if (isPendingLambdaTryOnStatus(status) || !matchingResult) {
          scheduleNextPoll();
          return;
        }

        stopPollingForPendingTask(persistedSession);
      } catch {
        if (!isActive) {
          return;
        }

        scheduleNextPoll();
      } finally {
        isPolling = false;

        if (shouldRunPollImmediately) {
          shouldRunPollImmediately = false;
          requestPoll();
        }
      }
    };

    requestPoll();

    return () => {
      isActive = false;
      clearScheduledPoll();
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(TRY_ON_SESSION_CHANGE_EVENT, handleSessionChange);
      window.removeEventListener('focus', handleSessionChange);
      window.removeEventListener('pageshow', handleSessionChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTryOnPollingOwnedByPage]);

  return null;
}
